import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Field, Page } from "../../components";
import { AuthLayout } from "../auth/AuthLayout";
import { ApiError } from "../../api/errors";
import { useFeatures } from "../../api/features";
import { useSession } from "../../api/session";
import { t } from "../../i18n/t";

type LocationState = { from?: string } | null;

/** §B1.3: one field, `type="password" autocomplete="off"`, calling `create_session`.
 *
 * Fable #8's belt-and-braces half: `RequireSession` now waits for `features.loading` before ever picking a
 * redirect target, so a fresh anonymous visitor should not land HERE on a multiuser deployment at all --
 * but a bookmark, a stale link, or a browser back button can still put one on this exact URL. When
 * `features.multiuser_enabled` is true, this screen therefore also offers a way off itself, to `/auth/login`,
 * rather than trusting the router alone to have kept every path away from a form that cannot work here.
 */
export function LoginScreen(): JSX.Element {
  const { status, login } = useSession();
  const { features } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as LocationState)?.from ?? "/";
  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(apiKey);
      setApiKey("");
      navigate(from, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "rate_limited") {
        const seconds = caught.details.retry_after_seconds;
        setError(t("login.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else {
        setError(t("login.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  // AuthFlows card 08: the operator-key card in the SignIn layout; the key field reads as a secret in mono.
  return (
    <AuthLayout>
      <Page narrow title={t("login.title")}>
        <form onSubmit={onSubmit} className="card card--auth">
          <Field label={t("login.field")} error={error}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="off"
                className="mono"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" className="button--auth" disabled={busy || apiKey === ""}>
            {t("login.submit")}
          </Button>
          {features.multiuser_enabled ? (
            <Link className="authcenter" to="/auth/login">
              {t("login.multiuser_hint")}
            </Link>
          ) : null}
        </form>
      </Page>
    </AuthLayout>
  );
}
